import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import PublicNavigation from '../../components/PublicNavigation';
import './ManualProjectsDashboard.css';

function ManualProjectsDashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [editingProject, setEditingProject] = useState(null);
  const [drillDownData, setDrillDownData] = useState(null);
  const [filters, setFilters] = useState({
    period: 'monthly',
    year: new Date().getFullYear(),
    month: '',
    quarter: '',
    chapter: '',
    district: '',
    category: ''
  });

  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const quarters = ['Q1 (Jan-Mar)', 'Q2 (Apr-Jun)', 'Q3 (Jul-Sep)', 'Q4 (Oct-Dec)'];

  useEffect(() => {
    loadProjects();
  }, []);

  async function loadProjects() {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'manualProjects'));
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setProjects(data);
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to load: ' + error.message);
    }
    setLoading(false);
  }

  const getFunds = (project) => {
    if (project.totalFunds) {
      return {
        received: project.totalFunds.received || 0,
        disbursed: project.totalFunds.disbursed || 0,
        openingBalance: project.totalFunds.openingBalance || 0,
        closingBalance: project.totalFunds.closingBalance || 0
      };
    }
    return {
      received: project.fundReceived || 0,
      disbursed: project.fundDisbursed || 0,
      openingBalance: project.fundOpeningBalance || 0,
      closingBalance: project.fundClosingBalance || 0
    };
  };

  const formatINR = (amount) => {
    return `₹${Math.round(amount).toLocaleString('en-IN')}`;
  };

  const countSchools = (projectDescription) => {
    if (!projectDescription) return 0;
    const lines = projectDescription.split('\n');
    let schoolCount = 0;
    lines.forEach(line => {
      if (/^\d+\./.test(line.trim())) {
        schoolCount++;
      }
    });
    return schoolCount;
  };

  const extractBeneficiaryNames = (beneficiaryName) => {
    if (!beneficiaryName) return [];
    return beneficiaryName.split(/[\n,]+/).map(n => n.trim()).filter(n => n.length > 0);
  };

  const getFilteredProjects = () => {
    let filtered = [...projects];

    if (filters.period === 'monthly' && filters.month) {
      filtered = filtered.filter(p => p.reportMonth === filters.month && p.reportYear === filters.year);
    } else if (filters.period === 'quarterly' && filters.quarter) {
      const qMonths = {
        'Q1 (Jan-Mar)': ['January','February','March'],
        'Q2 (Apr-Jun)': ['April','May','June'],
        'Q3 (Jul-Sep)': ['July','August','September'],
        'Q4 (Oct-Dec)': ['October','November','December']
      };
      filtered = filtered.filter(p => qMonths[filters.quarter].includes(p.reportMonth) && p.reportYear === filters.year);
    } else if (filters.period === 'yearly') {
      filtered = filtered.filter(p => p.reportYear === filters.year);
    }

    if (filters.chapter) filtered = filtered.filter(p => p.chapter === filters.chapter);
    if (filters.district) filtered = filtered.filter(p => p.district === filters.district);
    if (filters.category) filtered = filtered.filter(p => p.category === filters.category);

    return filtered;
  };

  const calculateMetrics = () => {
    const filtered = getFilteredProjects();
    let totalSchools = 0;
    let totalBeneficiaryNames = 0;
    let totalFundsReceived = 0;
    let totalFundsDisbursed = 0;
    
    filtered.forEach(p => {
      totalSchools += countSchools(p.projectDescription);
      totalBeneficiaryNames += extractBeneficiaryNames(p.beneficiaryName).length;
      const funds = getFunds(p);
      totalFundsReceived += funds.received;
      totalFundsDisbursed += funds.disbursed;
    });

    return {
      totalProjects: filtered.length,
      totalSchools,
      totalBeneficiaries: filtered.reduce((sum, p) => sum + (p.totalBeneficiaries || 0), 0),
      totalBeneficiaryNames,
      totalFundsReceived,
      totalFundsDisbursed,
      activeProjects: filtered.filter(p => p.status === 'Active').length,
      completedProjects: filtered.filter(p => p.status === 'Completed').length
    };
  };

  const getChapterReport = () => {
    const filtered = getFilteredProjects();
    const byChapter = {};
    
    filtered.forEach(p => {
      const ch = p.chapter || 'No Chapter';
      if (!byChapter[ch]) {
        byChapter[ch] = { 
          projects: 0, 
          schools: 0,
          beneficiaries: 0, 
          beneficiaryNames: 0,
          fundsReceived: 0, 
          fundsDisbursed: 0,
          districts: new Set(),
          categories: new Set()
        };
      }
      byChapter[ch].projects++;
      byChapter[ch].schools += countSchools(p.projectDescription);
      byChapter[ch].beneficiaries += p.totalBeneficiaries || 0;
      byChapter[ch].beneficiaryNames += extractBeneficiaryNames(p.beneficiaryName).length;
      
      const funds = getFunds(p);
      byChapter[ch].fundsReceived += funds.received;
      byChapter[ch].fundsDisbursed += funds.disbursed;
      
      if (p.district) byChapter[ch].districts.add(p.district);
      if (p.category) byChapter[ch].categories.add(p.category);
    });

    return Object.entries(byChapter).map(([name, data]) => ({ 
      name, 
      ...data,
      districtCount: data.districts.size,
      categoryCount: data.categories.size
    }));
  };

  const getDistrictReport = () => {
    const filtered = getFilteredProjects();
    const byDistrict = {};
    
    filtered.forEach(p => {
      const dist = p.district || 'Unknown';
      if (!byDistrict[dist]) {
        byDistrict[dist] = { 
          projects: 0, 
          schools: 0,
          beneficiaries: 0,
          beneficiaryNames: 0,
          fundsReceived: 0, 
          fundsDisbursed: 0 
        };
      }
      byDistrict[dist].projects++;
      byDistrict[dist].schools += countSchools(p.projectDescription);
      byDistrict[dist].beneficiaries += p.totalBeneficiaries || 0;
      byDistrict[dist].beneficiaryNames += extractBeneficiaryNames(p.beneficiaryName).length;
      
      const funds = getFunds(p);
      byDistrict[dist].fundsReceived += funds.received;
      byDistrict[dist].fundsDisbursed += funds.disbursed;
    });

    return Object.entries(byDistrict).map(([name, data]) => ({ name, ...data })).sort((a,b) => b.projects - a.projects);
  };

  const getCategoryReport = () => {
    const filtered = getFilteredProjects();
    const byCategory = {};
    
    filtered.forEach(p => {
      const cat = p.category || 'Uncategorized';
      if (!byCategory[cat]) {
        byCategory[cat] = { 
          projects: 0, 
          schools: 0,
          beneficiaries: 0,
          beneficiaryNames: 0,
          fundsReceived: 0 
        };
      }
      byCategory[cat].projects++;
      byCategory[cat].schools += countSchools(p.projectDescription);
      byCategory[cat].beneficiaries += p.totalBeneficiaries || 0;
      byCategory[cat].beneficiaryNames += extractBeneficiaryNames(p.beneficiaryName).length;
      
      const funds = getFunds(p);
      byCategory[cat].fundsReceived += funds.received;
    });

    return Object.entries(byCategory).map(([name, data]) => ({ name, ...data }));
  };

  const showDrillDown = (chapterName) => {
    const filtered = getFilteredProjects().filter(p => (p.chapter || 'No Chapter') === chapterName);
    const byDistrictCategory = {};

    filtered.forEach(p => {
      const dist = p.district || 'Unknown';
      const cat = p.category || 'Uncategorized';
      const key = `${dist}|${cat}`;
      
      if (!byDistrictCategory[key]) {
        byDistrictCategory[key] = {
          district: dist,
          category: cat,
          projects: 0,
          schools: 0,
          beneficiaries: 0,
          beneficiaryNames: 0
        };
      }
      byDistrictCategory[key].projects++;
      byDistrictCategory[key].schools += countSchools(p.projectDescription);
      byDistrictCategory[key].beneficiaries += p.totalBeneficiaries || 0;
      byDistrictCategory[key].beneficiaryNames += extractBeneficiaryNames(p.beneficiaryName).length;
    });

    setDrillDownData({
      chapterName,
      data: Object.values(byDistrictCategory)
    });
  };

  const handleEdit = (project) => {
    setEditingProject({...project});
  };

  const handleUpdate = async () => {
    try {
      const { id, createdAt, createdBy, ...data } = editingProject;
      await updateDoc(doc(db, 'manualProjects', id), data);
      alert('✅ Updated successfully!');
      setEditingProject(null);
      loadProjects();
    } catch (error) {
      alert('Failed: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project? Cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'manualProjects', id));
      alert('✅ Deleted!');
      loadProjects();
    } catch (error) {
      alert('Failed: ' + error.message);
    }
  };

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      alert('No data to export');
      return;
    }
    const csv = [
      Object.keys(data[0]).join(','),
      ...data.map(row => Object.values(row).map(v => `"${v}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const metrics = calculateMetrics();
  const chapterReport = getChapterReport();
  const districtReport = getDistrictReport();
  const categoryReport = getCategoryReport();
  const filteredProjects = getFilteredProjects();

  const uniqueChapters = [...new Set(projects.map(p => p.chapter).filter(Boolean))].sort();
  const uniqueDistricts = [...new Set(projects.map(p => p.district).filter(Boolean))].sort();
  const uniqueCategories = [...new Set(projects.map(p => p.category).filter(Boolean))].sort();

  if (loading) return (
    <div className="dashboard-page">
      <PublicNavigation />
      <div style={{textAlign:'center',padding:'100px',fontSize:'24px',color:'#667eea'}}>
        Loading projects...
      </div>
    </div>
  );

  return (
    <div className="dashboard-page">
      <PublicNavigation />
      
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1>📊 Manual Projects Dashboard</h1>
          <p>Executive reporting with monthly, quarterly, and yearly views</p>
        </div>

        {/* Filters */}
        <div className="filters-panel">
          <div className="filter-group">
            <label>Period</label>
            <select value={filters.period} onChange={(e) => setFilters({...filters, period: e.target.value, month: '', quarter: ''})}>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Year</label>
            <input type="number" value={filters.year} onChange={(e) => setFilters({...filters, year: parseInt(e.target.value)})} min="2020" max="2030" />
          </div>

          {filters.period === 'monthly' && (
            <div className="filter-group">
              <label>Month</label>
              <select value={filters.month} onChange={(e) => setFilters({...filters, month: e.target.value})}>
                <option value="">All Months</option>
                {months.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
          )}

          {filters.period === 'quarterly' && (
            <div className="filter-group">
              <label>Quarter</label>
              <select value={filters.quarter} onChange={(e) => setFilters({...filters, quarter: e.target.value})}>
                <option value="">All Quarters</option>
                {quarters.map(q => <option key={q}>{q}</option>)}
              </select>
            </div>
          )}

          <div className="filter-group">
            <label>Chapter</label>
            <select value={filters.chapter} onChange={(e) => setFilters({...filters, chapter: e.target.value})}>
              <option value="">All Chapters</option>
              {uniqueChapters.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>

          <div className="filter-group">
            <label>District</label>
            <select value={filters.district} onChange={(e) => setFilters({...filters, district: e.target.value})}>
              <option value="">All Districts</option>
              {uniqueDistricts.map(d => <option key={d}>{d}</option>)}
            </select>
          </div>

          <div className="filter-group">
            <label>Category</label>
            <select value={filters.category} onChange={(e) => setFilters({...filters, category: e.target.value})}>
              <option value="">All Categories</option>
              {uniqueCategories.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {/* Metrics */}
        <div className="metrics-grid">
          <div className="metric-card blue">
            <div className="metric-icon">📁</div>
            <div className="metric-value">{metrics.totalProjects}</div>
            <div className="metric-label">Total Projects</div>
          </div>
          <div className="metric-card green">
            <div className="metric-icon">🏫</div>
            <div className="metric-value">{metrics.totalSchools}</div>
            <div className="metric-label">Schools</div>
          </div>
          <div className="metric-card teal">
            <div className="metric-icon">👥</div>
            <div className="metric-value">{metrics.totalBeneficiaries.toLocaleString('en-IN')}</div>
            <div className="metric-label">Beneficiaries</div>
          </div>
          <div className="metric-card indigo">
            <div className="metric-icon">📝</div>
            <div className="metric-value">{metrics.totalBeneficiaryNames}</div>
            <div className="metric-label">Named Beneficiaries</div>
          </div>
          <div className="metric-card purple">
            <div className="metric-icon">💵</div>
            <div className="metric-value">{formatINR(metrics.totalFundsReceived)}</div>
            <div className="metric-label">Received</div>
          </div>
          <div className="metric-card orange">
            <div className="metric-icon">💸</div>
            <div className="metric-value">{formatINR(metrics.totalFundsDisbursed)}</div>
            <div className="metric-label">Disbursed</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs-nav">
          <button className={activeTab === 'overview' ? 'active' : ''} onClick={() => setActiveTab('overview')}>📋 Overview</button>
          <button className={activeTab === 'projects' ? 'active' : ''} onClick={() => setActiveTab('projects')}>📝 All Projects</button>
          <button className={activeTab === 'chapter' ? 'active' : ''} onClick={() => setActiveTab('chapter')}>🏢 Chapter Report</button>
          <button className={activeTab === 'district' ? 'active' : ''} onClick={() => setActiveTab('district')}>📍 District Report</button>
          <button className={activeTab === 'category' ? 'active' : ''} onClick={() => setActiveTab('category')}>📂 Category Report</button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'overview' && (
            <div className="overview-section">
              <div className="status-cards">
                <div className="status-card active-card">
                  <h3>Active Projects</h3>
                  <div className="big-number">{metrics.activeProjects}</div>
                </div>
                <div className="status-card completed-card">
                  <h3>Completed Projects</h3>
                  <div className="big-number">{metrics.completedProjects}</div>
                </div>
              </div>

              <div className="top-section">
                <h3>Top 5 Districts by Projects</h3>
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>District</th>
                      <th>Projects</th>
                      <th>Schools</th>
                      <th>Beneficiaries</th>
                    </tr>
                  </thead>
                  <tbody>
                    {districtReport.slice(0, 5).map(d => (
                      <tr key={d.name}>
                        <td><strong>{d.name}</strong></td>
                        <td>{d.projects}</td>
                        <td>{d.schools}</td>
                        <td>{d.beneficiaries.toLocaleString('en-IN')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'projects' && (
            <div>
              <div className="section-header">
                <h3>All Projects ({filteredProjects.length})</h3>
                <button onClick={() => exportToCSV(filteredProjects.map(p => {
                  const funds = getFunds(p);
                  return {
                    Category: p.category,
                    District: p.district,
                    Donor: p.donorName,
                    Schools: countSchools(p.projectDescription),
                    Beneficiaries: p.totalBeneficiaries,
                    Named: extractBeneficiaryNames(p.beneficiaryName).length,
                    Received: funds.received,
                    Status: p.status
                  };
                }), 'all_projects')} className="export-btn">📥 Export CSV</button>
              </div>
              <div className="table-scroll">
                <table className="projects-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>District</th>
                      <th>Donor</th>
                      <th>Schools</th>
                      <th>Beneficiaries</th>
                      <th>Received</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProjects.map(p => {
                      const funds = getFunds(p);
                      return (
                        <tr key={p.id}>
                          <td>{p.category}</td>
                          <td>{p.district}</td>
                          <td>{p.donorName}</td>
                          <td>{countSchools(p.projectDescription)}</td>
                          <td>{p.totalBeneficiaries || 0}</td>
                          <td>{formatINR(funds.received)}</td>
                          <td><span className={`badge ${p.status?.toLowerCase()}`}>{p.status}</span></td>
                          <td>
                            <button onClick={() => handleEdit(p)} className="btn-edit">✏️</button>
                            <button onClick={() => handleDelete(p.id)} className="btn-delete">🗑️</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'chapter' && (
            <div>
              <div className="section-header">
                <h3>Chapter-wise Report</h3>
                <button onClick={() => exportToCSV(chapterReport, 'chapter_report')} className="export-btn">📥 Export CSV</button>
              </div>
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Chapter</th>
                    <th>Projects</th>
                    <th>Schools</th>
                    <th>Districts</th>
                    <th>Categories</th>
                    <th>Beneficiaries</th>
                    <th>Named</th>
                    <th>Funds Received</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {chapterReport.map(c => (
                    <tr key={c.name}>
                      <td><strong>{c.name}</strong></td>
                      <td>{c.projects}</td>
                      <td>{c.schools}</td>
                      <td>{c.districtCount}</td>
                      <td>{c.categoryCount}</td>
                      <td>{c.beneficiaries.toLocaleString('en-IN')}</td>
                      <td>{c.beneficiaryNames}</td>
                      <td>{formatINR(c.fundsReceived)}</td>
                      <td>
                        <button onClick={() => showDrillDown(c.name)} className="drill-btn">🔍 Details</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'district' && (
            <div>
              <div className="section-header">
                <h3>District-wise Report</h3>
                <button onClick={() => exportToCSV(districtReport, 'district_report')} className="export-btn">📥 Export CSV</button>
              </div>
              <table className="report-table">
                <thead>
                  <tr>
                    <th>District</th>
                    <th>Projects</th>
                    <th>Schools</th>
                    <th>Beneficiaries</th>
                    <th>Named</th>
                    <th>Funds Received</th>
                    <th>Funds Disbursed</th>
                  </tr>
                </thead>
                <tbody>
                  {districtReport.map(d => (
                    <tr key={d.name}>
                      <td><strong>{d.name}</strong></td>
                      <td>{d.projects}</td>
                      <td>{d.schools}</td>
                      <td>{d.beneficiaries.toLocaleString('en-IN')}</td>
                      <td>{d.beneficiaryNames}</td>
                      <td>{formatINR(d.fundsReceived)}</td>
                      <td>{formatINR(d.fundsDisbursed)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'category' && (
            <div>
              <div className="section-header">
                <h3>Category-wise Report</h3>
                <button onClick={() => exportToCSV(categoryReport, 'category_report')} className="export-btn">📥 Export CSV</button>
              </div>
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Projects</th>
                    <th>Schools</th>
                    <th>Beneficiaries</th>
                    <th>Named</th>
                    <th>Funds Received</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryReport.map(c => (
                    <tr key={c.name}>
                      <td><strong>{c.name}</strong></td>
                      <td>{c.projects}</td>
                      <td>{c.schools}</td>
                      <td>{c.beneficiaries.toLocaleString('en-IN')}</td>
                      <td>{c.beneficiaryNames}</td>
                      <td>{formatINR(c.fundsReceived)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Drill Down Modal */}
      {drillDownData && (
        <div className="modal" onClick={() => setDrillDownData(null)}>
          <div className="modal-box drill-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{drillDownData.chapterName} - District & Category Breakdown</h2>
            <table className="report-table">
              <thead>
                <tr>
                  <th>District</th>
                  <th>Category</th>
                  <th>Projects</th>
                  <th>Schools</th>
                  <th>Beneficiaries</th>
                  <th>Named</th>
                </tr>
              </thead>
              <tbody>
                {drillDownData.data.map((item, idx) => (
                  <tr key={idx}>
                    <td><strong>{item.district}</strong></td>
                    <td>{item.category}</td>
                    <td>{item.projects}</td>
                    <td>{item.schools}</td>
                    <td>{item.beneficiaries}</td>
                    <td>{item.beneficiaryNames}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="modal-actions">
              <button onClick={() => setDrillDownData(null)} className="btn-cancel">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingProject && (
        <div className="modal" onClick={() => setEditingProject(null)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Project</h2>
            <div className="form-grid-modal">
              <div>
                <label>Category</label>
                <input value={editingProject.category || ''} onChange={(e) => setEditingProject({...editingProject, category: e.target.value})} />
              </div>
              <div>
                <label>District</label>
                <input value={editingProject.district || ''} onChange={(e) => setEditingProject({...editingProject, district: e.target.value})} />
              </div>
              <div>
                <label>Donor</label>
                <input value={editingProject.donorName || ''} onChange={(e) => setEditingProject({...editingProject, donorName: e.target.value})} />
              </div>
              <div>
                <label>Status</label>
                <select value={editingProject.status || 'Active'} onChange={(e) => setEditingProject({...editingProject, status: e.target.value})}>
                  <option>Planning</option>
                  <option>Active</option>
                  <option>Completed</option>
                  <option>On Hold</option>
                </select>
              </div>
              <div>
                <label>Beneficiaries</label>
                <input type="number" value={editingProject.totalBeneficiaries || 0} onChange={(e) => setEditingProject({...editingProject, totalBeneficiaries: parseInt(e.target.value)})} />
              </div>
              <div className="full-width">
                <label>Description</label>
                <textarea value={editingProject.projectDescription || ''} onChange={(e) => setEditingProject({...editingProject, projectDescription: e.target.value})} rows="3" />
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={() => setEditingProject(null)} className="btn-cancel">Cancel</button>
              <button onClick={handleUpdate} className="btn-save">Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManualProjectsDashboard;
